import React, {useEffect, useState} from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
    Box,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    Paper,
    IconButton,
    Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {getRequest} from "../../Fetch/request";
import Loader from '../Loader/Loader';

const problems = [
];

const ProblemDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { topic } = useParams();
    const [problems, setProblems] = useState([]);
    const [problemLoading, setIsProblemLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(25);
    const [totalProblems, setTotalProblems] = useState(0);
    const [selectedTopic, setSelectedTopic] = useState(topic || null);
    const totalPages = Math.max(1, Math.ceil(totalProblems / rowsPerPage));

    const handleNavigate = (problemId) => {
        navigate(`/problem-statement?problemId=${problemId}`);
    };

    // Combined effect to handle route parameters and fetch data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
                // Note: We now allow non-authenticated users to view DSA problems

                // Use topic from route parameter or fallback to query parameter for backward compatibility
                const topicFromRoute = topic;
                const topicFromQuery = new URLSearchParams(location.search).get('topic');
                const currentTopic = topicFromRoute || topicFromQuery;
                
                // Update selectedTopic if it's different
                if (currentTopic && currentTopic !== selectedTopic) {
                    setSelectedTopic(currentTopic);
                    return; // Exit early, let the next render handle the API call
                }

                let url = `${process.env.REACT_APP_URL}/problems?userId=${userId}&page=${page + 1}&limit=${rowsPerPage}`;
                
                // Add topicId filter if selected
                if (selectedTopic) {
                    url += `&topicId=${encodeURIComponent(selectedTopic)}`;
                }
                
                setIsProblemLoading(true);
                const getResponse = await getRequest(url);
                setIsProblemLoading(false);
                if (getResponse.status === "200" && getResponse.response !== null) {
                    const { problems: fetchedProblems = [], meta = {} } = getResponse.response;
                    const transformedProblems = fetchedProblems.map(problem => ({
                        problemId: problem.problemId,
                        title: problem.title,
                        problemRating: problem.problemRating || problem.problemMetric?.problemRating,
                        status: problem.status || 'NOT_ATTEMPTED'
                    }));
                    setProblems(transformedProblems);
                    setTotalProblems(meta.totalProblems || fetchedProblems.length);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        
        fetchData();
    }, [page, rowsPerPage, selectedTopic, topic, location.search]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    return (
        <Container maxWidth="lg" sx={{ marginTop: '20px' }}>
            
            <Paper elevation={0} sx={{ borderRadius: '8px', overflow: 'hidden', backgroundColor: 'transparent' }}>
              <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>
                                Status
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>Title</TableCell>

                            <TableCell align="center" sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>
                                Rating
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                    {problemLoading ? (
                        <TableRow>
                            <TableCell colSpan={3} sx={{ border: 'none' }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center',
                                    minHeight: '150px'
                                }}>
                                    <Loader />
                                </Box>
                            </TableCell>
                        </TableRow>
                    ) : (
                        problems.length > 0 ? (
                            problems.map((problem) => (
                            <TableRow key={problem.problemId} hover>
                                {/* Status */}
                                <TableCell align="center" sx={{ py: 0.5 }}>
                                    {problem.status === 'Correct' && (
                                        <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '1.2rem' }} />
                                    )}
                                    {problem.status !== 'Correct' && problem.status !== 'Not Attempted' && (
                                        <CancelIcon sx={{ color: '#F44336', fontSize: '1.2rem' }} />
                                    )}
                                    {problem.status === 'Not Attempted' && (
                                        <HourglassEmptyIcon sx={{ color: '#666', fontSize: '1.2rem' }} />
                                    )}
                                </TableCell>

                                {/* Title (Clickable Text) */}
                                <TableCell
                                    sx={{
                                        cursor: 'pointer',
                                        color: 'primary.main',
                                        textDecoration: 'none',
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        letterSpacing: '0.01em',
                                        py: 0.5,
                                    }}
                                >
                                    <a
                                        href={`/problem-statement?problemId=${problem.problemId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', letterSpacing: '0.01em' }}
                                    >
                                        {problem.title}
                                    </a>
                                </TableCell>



                                {/* Problem Rating */}
                                <TableCell align="center" sx={{ py: 0.5 }}>
                                    <Typography variant="caption" sx={{ 
                                        fontSize: '0.8rem',
                                        fontWeight: 'bold',
                                        color: problem.problemRating >= 1600 ? '#F44336' : 
                                               problem.problemRating >= 1500 ? '#FF9800' : '#4CAF50'
                                    }}>
                                        {problem.problemRating ? problem.problemRating : 'N/A'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 2 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>No problems found.</Typography>
                                </TableCell>
                            </TableRow>
                        )
                    )}
                    </TableBody>
                </Table>
            </TableContainer>
              {/* Bottom navigation arrows */}
              <Box sx={{ display:'flex', justifyContent:'flex-end', alignItems:'center', p:0.5, gap:0.5 }}>
                <IconButton aria-label="previous page" size="small" disabled={page===0} onClick={(e)=>handleChangePage(e,page-1)}>
                  <ArrowBackIosNewIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
                <Typography variant="caption" sx={{ minWidth:50, textAlign:'center', fontSize: '0.8rem' }}>
                  {`${page+1} / ${totalPages}`}
                </Typography>
                <IconButton aria-label="next page" size="small" disabled={page >= totalPages-1} onClick={(e)=>handleChangePage(e,page+1)}>
                  <ArrowForwardIosIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Box>
            </Paper>
        </Container>
    );
};

export default ProblemDetails;
